import MessageFormatter from '../helpers/MessageFormatter';
import { stripDiacritics } from '../helpers/textSearch';

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
const escapeRegExp = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Builds a diacritic-stripped projection of `text` plus an index map so
// matches in the stripped string can be resolved back to original slices.
const buildStrippedIndex = text => {
  let stripped = '';
  const indexMap = [];
  for (let i = 0; i < text.length; i += 1) {
    const mapped = stripDiacritics(text[i]);
    for (let j = 0; j < mapped.length; j += 1) {
      stripped += mapped[j];
      indexMap.push(i);
    }
  }
  return { stripped, indexMap };
};

const wrapMatches = (text, stripped, indexMap, regex, highlightClass) => {
  let result = '';
  let cursor = 0;
  let match = regex.exec(stripped);
  while (match !== null) {
    if (match[0].length === 0) {
      regex.lastIndex += 1;
    } else {
      const start = indexMap[match.index];
      const end = indexMap[match.index + match[0].length - 1] + 1;
      result += text.slice(cursor, start);
      result += `<span class="${highlightClass}">${text.slice(start, end)}</span>`;
      cursor = end;
    }
    match = regex.exec(stripped);
  }
  return result + text.slice(cursor);
};

/**
 * A composable providing utility functions for message formatting.
 *
 * @returns {Object} A set of functions for message formatting.
 */
export const useMessageFormatter = () => {
  /**
   * Formats a message based on specified conditions.
   *
   * @param {string} message - The message to be formatted.
   * @param {boolean} isATweet - Whether the message is a tweet.
   * @param {boolean} isAPrivateNote - Whether the message is a private note.
   * @returns {string} - The formatted message.
   */
  // TODO: ref:https://github.com/chatwoot/chatwoot/pull/10725#discussion_r1925300874
  const formatMessage = (message, isATweet, isAPrivateNote, linkify) => {
    const messageFormatter = new MessageFormatter(
      message,
      isATweet,
      isAPrivateNote,
      linkify
    );
    return messageFormatter.formattedMessage;
  };

  /**
   * Converts a message to plain text.
   *
   * @param {string} message - The message to be converted.
   * @param {boolean} isATweet - Whether the message is a tweet.
   * @returns {string} - The plain text message.
   */
  const getPlainText = (message, isATweet) => {
    const messageFormatter = new MessageFormatter(message, isATweet);
    return messageFormatter.plainText;
  };

  /**
   * Truncates a description to a maximum length of 100 characters.
   *
   * @param {string} [description=''] - The description to be truncated.
   * @returns {string} - The truncated description.
   */
  const truncateMessage = (description = '') => {
    if (description.length < 100) {
      return description;
    }

    return `${description.slice(0, 97)}...`;
  };

  /**
   * Highlights occurrences of a search term within given content.
   *
   * @param {string} [content=''] - The content in which to search.
   * @param {string} [searchTerm=''] - The term to search for.
   * @param {string} [highlightClass=''] - The CSS class to apply to the highlighted term.
   * @returns {string} - The content with highlighted terms.
   */
  const highlightContent = (
    content = '',
    searchTerm = '',
    highlightClass = ''
  ) => {
    const plainTextContent = getPlainText(content);

    // Backend search uses PostgreSQL unaccent(), so matches may contain
    // diacritics the literal query doesn't. Match on diacritic-stripped
    // projections of both sides, but wrap the original accented slices.
    const strippedTerm = stripDiacritics(searchTerm.trim());
    if (!strippedTerm) return plainTextContent;

    const { stripped, indexMap } = buildStrippedIndex(plainTextContent);
    const regex = new RegExp(escapeRegExp(strippedTerm), 'ig');
    return wrapMatches(
      plainTextContent,
      stripped,
      indexMap,
      regex,
      highlightClass
    );
  };

  return {
    formatMessage,
    getPlainText,
    truncateMessage,
    highlightContent,
  };
};
